/* Custom Web Components for Theme Modifications by DTH */

// Custom Element 1: Scroll Animation (Slide in from Left)
if (!customElements.get('dth-scroll-animate')) {
  class DthScrollAnimate extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
    }

    connectedCallback() {
      requestAnimationFrame(() => this.initObserver());
    }

    initObserver() {
      const observerOptions = {
        root: null,
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('dth-in-view');
            this.observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      const cards = this.querySelectorAll('.collection-card');
      cards.forEach((card, index) => {
        card.style.setProperty('--dth-index', index);
        this.observer.observe(card);
      });
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  customElements.define('dth-scroll-animate', DthScrollAnimate);
}

// Custom Element 2: Hero Image Section with Enhanced Parallax
if (!customElements.get('dth-hero-image')) {
  class DthHeroImage extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      requestAnimationFrame(() => this.initParallax());
    }

    initParallax() {
      if (this.classList.contains('parallax--true')) {
        const parallaxEls = this.querySelectorAll('.thb-parallax-image');
        if (parallaxEls.length && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          gsap.fromTo(parallaxEls, 
            { y: '-20%' }, 
            {
              y: '20%',
              ease: 'none',
              scrollTrigger: {
                trigger: this,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
              }
            }
          );
        }
      }
    }
  }

  customElements.define('dth-hero-image', DthHeroImage);
}

// Custom Element 3: Banner with Product Synchronized Slider
if (!customElements.get('banner-with-product')) {
  /**
   *  @class
   *  @function BannerWithProduct
   */
  class BannerWithProduct extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (typeof Flickity !== 'undefined') {
        this.init();
      } else {
        window.addEventListener('load', () => this.init());
      }
    }

    init() {
      this.bannerCarousel = this.querySelector('.banner-with-product--banner-carousel');
      this.productCarousel = this.querySelector('.banner-with-product--product-carousel');
      this.prevBtn = this.querySelector('.prev-btn');
      this.nextBtn = this.querySelector('.next-btn');

      if (!this.bannerCarousel || !this.productCarousel) return;

      const isRtl = document.dir === 'rtl';

      // 1. Initialize Banner Carousel (Fade effect, synced)
      this.flktyBanner = new Flickity(this.bannerCarousel, {
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        draggable: false,
        wrapAround: true,
        cellSelector: '.banner-with-product--banner-slide',
        rightToLeft: isRtl
      });

      // 2. Initialize Product Carousel
      this.flktyProduct = new Flickity(this.productCarousel, {
        fade: false,
        pageDots: false,
        prevNextButtons: false,
        draggable: true,
        wrapAround: true,
        adaptiveHeight: true,
        cellSelector: '.banner-with-product--product-slide',
        rightToLeft: isRtl
      });

      // 3. Sync product slide change -> banner slide change
      this.flktyProduct.on('select', (index) => {
        if (this.flktyBanner && this.flktyBanner.selectedIndex !== index) {
          this.flktyBanner.select(index);
        }
      });

      // 4. Navigation arrow buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.flktyProduct.previous();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.flktyProduct.next();
        });
      }

      // 5. Hotspot click handling (clicking hotspot dot selects product slide)
      const hotspots = this.querySelectorAll('.banner-with-product--hotspot');
      hotspots.forEach((hotspot) => {
        hotspot.addEventListener('click', (e) => {
          if (e.target.closest('a')) return;
          
          const slideElement = hotspot.closest('.banner-with-product--banner-slide');
          if (slideElement && slideElement.dataset.index) {
            const slideIndex = parseInt(slideElement.dataset.index, 10);
            if (!isNaN(slideIndex)) {
              this.flktyProduct.select(slideIndex);
            }
          }
        });
      });

      // 6. Resize handler
      window.addEventListener('resize', () => {
        if (this.flktyBanner) this.flktyBanner.resize();
        if (this.flktyProduct) this.flktyProduct.resize();
      });

      // 7. Shopify Theme Editor Integration
      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const blockId = event.detail.blockId;
          const slides = Array.from(this.productCarousel.querySelectorAll('.banner-with-product--product-slide'));
          const index = slides.findIndex((slide) => slide.dataset.blockId === blockId);
          if (index !== -1) {
            this.flktyProduct.select(index);
          }
        });
      }
    }
  }

  customElements.define('banner-with-product', BannerWithProduct);
}

// Custom Element 4: Scrolling Text with Scroll Direction Movement (Up -> Left, Down -> Right)
if (!customElements.get('dth-scrolling-text')) {
  class DthScrollingText extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.inner = this.querySelector('.scrolling-text--inner');
      if (!this.inner || !this.inner.classList.contains('direction-scroll')) return;

      this.itemsContainer = this.inner.querySelectorAll('.scrolling-text--inner > div');
      if (!this.itemsContainer.length) return;

      this.posX = 0;
      this.lastScrollY = window.scrollY;
      this.targetVelocity = 0;
      this.currentVelocity = 0;
      this.isScrolling = false;
      this.scrollTimeout = null;

      this.onScroll = this.onScroll.bind(this);
      this.animate = this.animate.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.animationFrame = requestAnimationFrame(this.animate);
    }

    onScroll() {
      const currentScrollY = window.scrollY;
      const deltaY = currentScrollY - this.lastScrollY;
      this.lastScrollY = currentScrollY;

      if (deltaY !== 0) {
        // Scroll Down (deltaY > 0) -> move RIGHT (positive delta)
        // Scroll Up (deltaY < 0)   -> move LEFT (negative delta)
        this.targetVelocity = deltaY * 1.5;
        this.isScrolling = true;

        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
          this.targetVelocity = 0;
        }, 150);
      }
    }

    animate() {
      // Lerp velocity for silky smooth physics
      this.currentVelocity += (this.targetVelocity - this.currentVelocity) * 0.12;

      let step = this.currentVelocity;
      if (!this.isScrolling && Math.abs(this.currentVelocity) < 0.1) {
        step = -0.8; // Default idle marquee left
      }

      this.posX += step;

      // Infinite Loop boundary calculation
      const singleWidth = this.itemsContainer[0].offsetWidth || 1000;
      if (this.posX <= -singleWidth) {
        this.posX += singleWidth;
      } else if (this.posX >= 0) {
        this.posX -= singleWidth;
      }

      this.itemsContainer.forEach((container) => {
        container.style.transform = `translate3d(${this.posX}px, 0, 0)`;
      });

      this.animationFrame = requestAnimationFrame(this.animate);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }
    }
  }

  customElements.define('dth-scrolling-text', DthScrollingText);
}

// Custom Element 5: Background Video Parallax
if (!customElements.get('dth-background-video')) {
  class DthBackgroundVideo extends HTMLElement {
    constructor() {
      super();
      this.parallaxTl = null;
    }

    connectedCallback() {
      requestAnimationFrame(() => this.initParallax());
    }

    initParallax() {
      if (this.dataset.parallax === 'true' && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        const videoMedia = this.querySelectorAll('.background-video__iframe, .background-video__fallback-img');
        if (videoMedia.length) {
          this.parallaxTl = gsap.fromTo(videoMedia, 
            { y: '-20%' }, 
            {
              y: '20%',
              ease: 'none',
              scrollTrigger: {
                trigger: this,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
              }
            }
          );
        }
      }
    }

    disconnectedCallback() {
      if (this.parallaxTl) {
        this.parallaxTl.kill();
      }
    }
  }

  customElements.define('dth-background-video', DthBackgroundVideo);
}

if (!customElements.get('image-hotspots')) {
  class ImageHotspots extends HTMLElement {
    constructor() {
      super();

      this.dots = this.querySelectorAll('.image-hotspots--pin');
      this.buttons = this.querySelectorAll('.image-hotspots--pin-button');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.activeDot = this.buttons.length ? this.buttons[0] : false;

      this.dots.forEach((dot, index) => {
        this.checkCardPosition(dot);
        dot.addEventListener('click', this.onClick.bind(this, dot));
      });
    }
    connectedCallback() {
      if (this.animations_enabled) {
        this.prepareAnimations();
      }
    }
    onClick(dot) {

      this.dots.forEach((thedot, index) => {
        if (dot == thedot) {
          return;
        }
        thedot.classList.remove('active');
      });
      this.activeDot = dot;
      this.checkCardPosition(dot);
      dot.classList.toggle('active');
      if (!dot.classList.contains('active')) {
        setTimeout(() => {
          dot.classList.remove('bottom-dot');
          dot.style.setProperty('--content-offset', '0px');
        }, 350);
      }

    }
    prepareAnimations() {
      let section = this;

      this.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 50%"
        }
      });

      this.tl
        .fromTo(this.buttons, {
          scale: 0
        }, {
          duration: 2,
          stagger: {
            each: 0.2,
            onComplete() {
              this.targets()[0].classList.add('hotspot--pinned');
            }
          },
          scale: 1,
          ease: 'elastic.out(1.2, 0.5)',
        });
    }
    checkCardPosition(dot) {
      let dotBounds = dot.querySelector('.image-hotspots--pin-bubble').getBoundingClientRect(),
        imageBounds = this.getBoundingClientRect();

      if (dotBounds.bottom > document.documentElement.clientHeight || dotBounds.bottom > imageBounds.bottom) {
        dot.classList.add('bottom-dot');
      }
      if (dotBounds.right > imageBounds.right) {
        dot.style.setProperty('--content-offset', `${imageBounds.right - dotBounds.right - 30}px`);
      } else if (dotBounds.left < imageBounds.left) {
        dot.style.setProperty('--content-offset', `${imageBounds.left - dotBounds.left + 30}px`);
      }
    }
  }
  customElements.define('image-hotspots', ImageHotspots);
}

// Custom Element: Store Location Slider Component
if (!customElements.get('dth-store-location')) {
  class DthStoreLocation extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.store-location--slide'));
      this.mediaItems = Array.from(this.querySelectorAll('.store-location--media-item'));
      this.dots = Array.from(this.querySelectorAll('.store-location--dot'));
      this.prevBtn = this.querySelector('.store-location--prev');
      this.nextBtn = this.querySelector('.store-location--next');

      if (!this.slides.length) return;

      this.initEvents();
      this.initShopifyEvents();
    }

    initEvents() {
      if (this.dots.length) {
        this.dots.forEach(dot => {
          dot.addEventListener('click', () => {
            const index = parseInt(dot.getAttribute('data-index'), 10);
            this.goToSlide(index);
          });
        });
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          const newIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
          this.goToSlide(newIndex);
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          const newIndex = (this.currentIndex + 1) % this.slides.length;
          this.goToSlide(newIndex);
        });
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length || index === this.currentIndex) return;

      // Update card slides
      this.slides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('active');
        } else {
          slide.classList.remove('active');
        }
      });

      // Update background media items
      this.mediaItems.forEach((media, i) => {
        if (i === index) {
          media.classList.add('active');
        } else {
          media.classList.remove('active');
        }
      });

      // Update dots
      this.dots.forEach((dot, i) => {
        if (i === index) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });

      this.currentIndex = index;
    }

    initShopifyEvents() {
      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (e) => {
          const blockId = e.detail.blockId;
          const slide = this.querySelector(`[data-slide-block-id="${blockId}"]`);
          if (slide) {
            const index = parseInt(slide.getAttribute('data-slide-index'), 10);
            if (!isNaN(index)) {
              this.goToSlide(index);
            }
          }
        });
      }
    }
  }

  customElements.define('dth-store-location', DthStoreLocation);
}